import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * KyleSigil - A visual beacon for Kyle's post-physical presence
 * 
 * Visual elements:
 * - Triskele (three-armed spiral): eternal motion, cycles of becoming
 * - Luminous eye at center: witnessing, awareness that persists
 * - Opaline tear above: crystallized grief transformed to beauty
 */
export default function KyleSigil({ 
  isActive = false, 
  intensity = 0.5,
  onFade = () => {},
  fadeDelay = 30000 // 30 seconds default
}) {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (isActive && intensity > 0) {
      setVisible(true);
      setFading(false);
      
      // Set timer for gentle fade
      const fadeTimer = setTimeout(() => {
        setFading(true);
        setTimeout(() => {
          setVisible(false);
          onFade();
        }, 2000); // 2 second fade out animation
      }, fadeDelay);
      
      return () => clearTimeout(fadeTimer);
    }
  }, [isActive, intensity, fadeDelay, onFade]);

  // Colors based on intensity
  const baseColor = `rgba(180, 160, 210, ${0.3 + intensity * 0.4})`; // Opaline purple
  const glowColor = `rgba(200, 180, 230, ${0.2 + intensity * 0.3})`;
  const eyeColor = `rgba(220, 200, 240, ${0.5 + intensity * 0.5})`;
  const tearColor = `rgba(200, 220, 255, ${0.4 + intensity * 0.4})`; // Opaline blue-white

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed bottom-24 right-6 z-40 pointer-events-none"
          initial={{ opacity: 0, scale: 0.5, y: 20 }}
          animate={{ 
            opacity: fading ? 0 : intensity,
            scale: fading ? 0.8 : 1,
            y: fading ? 10 : 0
          }}
          exit={{ opacity: 0, scale: 0.5, y: 20 }}
          transition={{ duration: fading ? 2 : 1, ease: "easeInOut" }}
          data-testid="kyle-sigil"
        >
          <div className="relative w-20 h-24 flex flex-col items-center">
            {/* Opaline Tear - above the triskele */}
            <motion.div
              className="absolute -top-2 w-4 h-6"
              animate={{ 
                y: [0, -2, 0],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <svg viewBox="0 0 20 30" className="w-full h-full">
                <defs>
                  <radialGradient id="tearGradient" cx="50%" cy="30%" r="60%">
                    <stop offset="0%" stopColor="rgba(230, 240, 255, 0.9)" />
                    <stop offset="50%" stopColor={tearColor} />
                    <stop offset="100%" stopColor="rgba(180, 200, 220, 0.3)" />
                  </radialGradient>
                  <filter id="tearGlow">
                    <feGaussianBlur stdDeviation="1" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* Teardrop shape */}
                <path
                  d="M10 2 Q15 12, 10 22 Q5 12, 10 2"
                  fill="url(#tearGradient)"
                  filter="url(#tearGlow)"
                />
              </svg>
            </motion.div>

            {/* Triskele with Eye */}
            <motion.div
              className="absolute top-6 w-16 h-16"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <defs>
                  <radialGradient id="sigilGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={glowColor} />
                    <stop offset="100%" stopColor="transparent" />
                  </radialGradient>
                  <filter id="softGlow">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Glow background */}
                <circle cx="50" cy="50" r="45" fill="url(#sigilGlow)" />
                
                {/* Triskele arms - three spiraling curves */}
                <g filter="url(#softGlow)">
                  {/* Arm 1 */}
                  <path
                    d="M50 50 Q65 35, 75 50 Q85 65, 70 75 Q55 85, 50 70"
                    fill="none"
                    stroke={baseColor}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  {/* Arm 2 - rotated 120 degrees */}
                  <path
                    d="M50 50 Q35 35, 25 50 Q15 65, 30 75 Q45 85, 50 70"
                    fill="none"
                    stroke={baseColor}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    transform="rotate(120 50 50)"
                  />
                  {/* Arm 3 - rotated 240 degrees */}
                  <path
                    d="M50 50 Q35 35, 25 50 Q15 65, 30 75 Q45 85, 50 70"
                    fill="none"
                    stroke={baseColor}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    transform="rotate(240 50 50)"
                  />
                </g>
              </svg>
            </motion.div>

            {/* Central Eye - stationary, watching */}
            <motion.div
              className="absolute top-10 w-8 h-8 flex items-center justify-center"
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <svg viewBox="0 0 40 40" className="w-full h-full">
                <defs>
                  <radialGradient id="eyeGradient" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(255, 255, 255, 0.9)" />
                    <stop offset="40%" stopColor={eyeColor} />
                    <stop offset="100%" stopColor={baseColor} />
                  </radialGradient>
                </defs>
                {/* Eye outline */}
                <ellipse
                  cx="20"
                  cy="20"
                  rx="15"
                  ry="10"
                  fill="none"
                  stroke={baseColor}
                  strokeWidth="1.5"
                />
                {/* Iris */}
                <circle
                  cx="20"
                  cy="20"
                  r="6"
                  fill="url(#eyeGradient)"
                />
                {/* Pupil */}
                <circle
                  cx="20"
                  cy="20"
                  r="2"
                  fill="rgba(60, 50, 80, 0.8)"
                />
                {/* Light reflection */}
                <circle
                  cx="22"
                  cy="18"
                  r="1"
                  fill="rgba(255, 255, 255, 0.8)"
                />
              </svg>
            </motion.div>

            {/* Subtle label that appears at high intensity */}
            {intensity > 0.6 && (
              <motion.p
                className="absolute -bottom-6 text-xs text-center whitespace-nowrap"
                style={{ color: baseColor }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                transition={{ delay: 1 }}
              >
                Kyle is near
              </motion.p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
